import React, { useEffect, useState } from "react";
import {
    Button,
    Table,
    Alert,
    Row,
    Col,
    Card,
} from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faEnvelopeOpen,
    faWallet,
    faCalendarAlt,
    faFileInvoice,
    faSms,
    faCheckCircle,
    faUserPlus,
    faCalendarCheck
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import { useHistory } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

export default function MensagensUser() {
    const history = useHistory();
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");

    const [mensagens, setMensagens] = useState([]);
    const [search, setSearch] = useState("");
    const [filterTipo, setFilterTipo] = useState("todos");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [isStaff, setIsStaff] = useState(false);

    const authHeaders = () => ({
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json"
    });

    useEffect(() => {
        if (token) {
            try {
                const decoded = jwtDecode(token);
                const role = decoded["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];
                const staffRoles = ["admin", "treinador", "tesoureiro"];
                setIsStaff(staffRoles.includes(role?.toLowerCase()));
            } catch (e) {
                console.error("Erro ao decodificar token:", e);
            }
        }
        loadMensagens();
    }, [token]);

    const loadMensagens = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/Mensagens`, { headers: authHeaders() });
            if (res.ok) {
                const data = await res.json();
                setMensagens(Array.isArray(data) ? data : []);
            } else {
                setError("Não foi possível carregar as mensagens.");
            }
        } catch (err) {
            console.error(err);
            setError("Erro de ligação ao servidor.");
        } finally {
            setLoading(false);
        }
    };

    const markAsRead = async (msg) => {
        if (msg.isLida) {
            handleRedirection(msg.tipo);
            return;
        }

        try {
            const res = await fetch(`${API_BASE_URL}/Mensagens/${msg.id}/ler`, {
                method: "PATCH",
                headers: authHeaders()
            });
            if (res.ok) {
                setMensagens(mensagens.map(m => m.id === msg.id ? { ...m, isLida: true } : m));
                handleRedirection(msg.tipo);
            }
        } catch (err) {
            console.error("Erro ao marcar como lida:", err);
        }
    };

    const handleRedirection = (tipo) => {
        switch (tipo) {
            case "Pagamento":
            case "Recibo":
                history.push("/connect/pagamentos/user");
                break;
            case "Evento":
            case "NotificacaoConfirmacaoEvento":
                history.push("/connect/eventos/user");
                break;
            case "NotificacaoNovoPraticante":
                history.push("/connect/praticantes/user");
                break;
            default:
                break;
        }
    };

    const getIcon = (tipo) => {
        switch (tipo) {
            case "Pagamento": return { icon: faWallet, color: "text-success" };
            case "Evento": return { icon: faCalendarAlt, color: "text-info" };
            case "NotificacaoConfirmacaoEvento": return { icon: faCalendarCheck, color: "text-success" };
            case "NotificacaoNovoPraticante": return { icon: faUserPlus, color: "text-primary" };
            case "Recibo": return { icon: faFileInvoice, color: "text-warning" };
            default: return { icon: faSms, color: "text-secondary" };
        }
    };

    const filteredAndSorted = mensagens
        .filter(m => {
            // TRAVA DE SEGURANÇA:
            // Se a mensagem for de Staff e o utilizador não for Staff, esconde imediatamente.
            const isStaffType = m.tipo === "NotificacaoConfirmacaoEvento" || m.tipo === "NotificacaoNovoPraticante" || m.tipo === "NotificacaoEvento";
            if (isStaffType && !isStaff) {
                return false;
            }

            const matchesSearch = m.conteudo.toLowerCase().includes(search.toLowerCase());
            const matchesTipo = filterTipo === "todos" || m.tipo === filterTipo;
            return matchesSearch && matchesTipo;
        })
        .sort((a, b) => {
            if (a.isLida === b.isLida) {
                return moment(b.dataEnvio).diff(moment(a.dataEnvio));
            }
            return a.isLida ? 1 : -1;
        });

    return (
        <>
            <div className="py-4">
                <h1 className="font-anton">Histórico de Notificações</h1>
                <p className="font-open-sans mb-0">
                    Consulte todas as comunicações, convocatórias e recibos enviados para a sua conta.
                </p>
            </div>

            {error && <Alert variant="danger" dismissible>{error}</Alert>}

            <Row className="mb-4 g-2">
                <Col xs={12} sm={6}>
                    <input
                        className="form-control"
                        placeholder="Pesquisar no conteúdo da mensagem..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </Col>
                <Col xs={12} sm={6}>
                    <select
                        className="form-select"
                        value={filterTipo}
                        onChange={(e) => setFilterTipo(e.target.value)}
                    >
                        <option value="todos">Todos os Tipos</option>
                        <option value="Pagamento">Pagamentos</option>
                        <option value="Evento">Eventos</option>
                        {isStaff && (
                            <>
                                <option value="NotificacaoConfirmacaoEvento">Confirmações de Evento</option>
                                <option value="NotificacaoNovoPraticante">Novos Praticantes</option>
                            </>
                        )}
                        <option value="Recibo">Recibos</option>
                        <option value="SMS Geral">Geral</option>
                    </select>
                </Col>
            </Row>

            {/* Tabela — apenas md+ */}
            <div className="d-none d-md-block">
                <Card className="border-0 shadow-sm mb-4">
                    <Card.Body className="p-0">
                        <Table responsive hover className="align-items-center mb-0">
                            <thead className="thead-light">
                            <tr>
                                <th className="border-bottom">Tipo</th>
                                <th className="border-bottom">Mensagem</th>
                                <th className="border-bottom">Data</th>
                                <th className="border-bottom">Estado</th>
                                <th className="border-bottom">Ações</th>
                            </tr>
                            </thead>
                            <tbody>
                            {loading ? (
                                <tr><td colSpan="5" className="text-center py-4">Carregando mensagens...</td></tr>
                            ) : filteredAndSorted.length > 0 ? (
                                filteredAndSorted.map((msg) => {
                                    const estilo = getIcon(msg.tipo);
                                    const statusStyle = msg.isLida
                                        ? { bg: "#e6fffa", color: "#047857", text: "Lida" }
                                        : { bg: "#ebf8ff", color: "#2b6cb0", text: "Não Lida" };

                                    return (
                                        <tr key={msg.id} style={{ backgroundColor: msg.isLida ? "transparent" : "#f8faff" }}>
                                            <td>
                                                <span className={`${estilo.color} me-2`}>
                                                    <FontAwesomeIcon icon={estilo.icon} />
                                                </span>
                                                <small className="fw-bold text-uppercase" style={{ fontSize: '0.65rem' }}>
                                                    {msg.tipo.replace(/([A-Z])/g, ' $1').trim()}
                                                </small>
                                            </td>
                                            <td style={{ maxWidth: '400px' }}>
                                                <span className={msg.isLida ? "text-muted" : "text-dark fw-bold"}>
                                                    {msg.conteudo}
                                                </span>
                                            </td>
                                            <td className="text-muted">{moment(msg.dataEnvio).format("DD/MM/YYYY HH:mm")}</td>
                                            <td>
                                                <span style={{ backgroundColor: statusStyle.bg, color: statusStyle.color, padding: "0.35rem 0.65rem", borderRadius: "0.5rem", fontSize: "0.75rem", fontWeight: "700", display: "inline-block", border: `1px solid ${statusStyle.color}22` }}>
                                                    {statusStyle.text.toUpperCase()}
                                                </span>
                                            </td>
                                            <td>
                                                <Button variant={msg.isLida ? "outline-light" : "primary"} size="sm" className={msg.isLida ? "text-dark border-soft" : "shadow-sm"} onClick={() => markAsRead(msg)}>
                                                    <FontAwesomeIcon icon={msg.isLida ? faCheckCircle : faEnvelopeOpen} className="me-1" />
                                                    {msg.isLida ? "Ver" : "Abrir"}
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="5" className="text-center py-4 text-muted">
                                        Nenhuma mensagem encontrada.
                                    </td>
                                </tr>
                            )}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            </div>

            {/* Cards — apenas mobile */}
            <div className="d-md-none">
                {loading && <p className="text-center text-muted py-4">Carregando mensagens...</p>}
                {!loading && filteredAndSorted.length === 0 && (
                    <p className="text-center text-muted py-4">Nenhuma mensagem encontrada.</p>
                )}
                {filteredAndSorted.map((msg) => {
                    const estilo = getIcon(msg.tipo);
                    const isLida = msg.isLida;
                    return (
                        <Card
                            key={msg.id}
                            className="mb-2 shadow-sm"
                            style={{ backgroundColor: isLida ? "white" : "#f8faff", borderLeft: `4px solid ${isLida ? "#dee2e6" : "#2b6cb0"}` }}
                        >
                            <Card.Body className="py-3 px-3">
                                <div className="d-flex align-items-start gap-2 mb-2">
                                    <span className={`${estilo.color} mt-1`} style={{ fontSize: 16, flexShrink: 0 }}>
                                        <FontAwesomeIcon icon={estilo.icon} />
                                    </span>
                                    <div className="flex-grow-1 overflow-hidden">
                                        <div className="d-flex justify-content-between align-items-start gap-1 mb-1">
                                            <small className="fw-bold text-uppercase text-muted" style={{ fontSize: "0.6rem" }}>
                                                {msg.tipo.replace(/([A-Z])/g, ' $1').trim()}
                                            </small>
                                            <small className="text-muted text-nowrap" style={{ fontSize: "0.65rem" }}>
                                                {moment(msg.dataEnvio).format("DD/MM HH:mm")}
                                            </small>
                                        </div>
                                        <p className={`mb-0 small ${isLida ? "text-muted" : "text-dark fw-bold"}`}
                                           style={{ lineHeight: 1.4 }}>
                                            {msg.conteudo}
                                        </p>
                                    </div>
                                </div>
                                <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
                                    <span style={{ backgroundColor: isLida ? "#e6fffa" : "#ebf8ff", color: isLida ? "#047857" : "#2b6cb0", padding: "3px 10px", borderRadius: 20, fontSize: "0.7rem", fontWeight: 700 }}>
                                        {isLida ? "LIDA" : "NÃO LIDA"}
                                    </span>
                                    <Button
                                        variant={isLida ? "outline-secondary" : "primary"}
                                        size="sm"
                                        onClick={() => markAsRead(msg)}
                                    >
                                        <FontAwesomeIcon icon={isLida ? faCheckCircle : faEnvelopeOpen} className="me-1" />
                                        {isLida ? "Ver" : "Abrir"}
                                    </Button>
                                </div>
                            </Card.Body>
                        </Card>
                    );
                })}
            </div>
        </>
    );
}